import { ButtonSelect, useLocalStorageAsState } from "@msys/ui";
import { useTranslate } from "@tolgee/react";
import React from "react";
import {
  PermissionName,
  WizardQuestionStrategy,
} from "../../../clients/graphqlTypes.js";
import { RestrictedByDocumentPermissionWithDebug } from "../../auth/RestrictedByDocumentPermission.js";
import { useStateWithUrlParam } from "../../commons/hooks/useStateWithUrlParam.js";
import {
  useQuoteWizardQuestionsQuery,
  useRequirementWizardQuestionsQuery,
} from "./WizardQuestions.generated.js";

export type WizardContext =
  | "item"
  | "onQuoteCreate"
  | "onQuoteRefinement"
  | "onQuoteRefinement2"
  | "onQuoteRefinement3";

export const WIZARD_CONTEXTS: ReadonlyArray<WizardContext> = [
  "item",
  "onQuoteCreate",
  "onQuoteRefinement",
  "onQuoteRefinement2",
  "onQuoteRefinement3",
];

export const DEFAULT_WIZARD_CONTEXT: WizardContext = "onQuoteRefinement";

/** The mode offered once the active one has nothing left to ask. */
export function getNextWizardContext(
  context: WizardContext
): WizardContext | null {
  return WIZARD_CONTEXTS[WIZARD_CONTEXTS.indexOf(context) + 1] ?? null;
}

/** The search parameter a link addresses the wizard mode with. */
export const WIZARD_MODE_URL_PARAM = "wizardMode";

/**
 * The URL wins so a link reproduces its mode; an unknown value falls back
 * rather than throwing, because the parameter is user-editable.
 */
export function resolveWizardContext(
  urlParamValue: string | null | undefined,
  storedValue: WizardContext | null | undefined
): WizardContext {
  if (isWizardContext(urlParamValue)) return urlParamValue;
  if (isWizardContext(storedValue)) return storedValue;
  return DEFAULT_WIZARD_CONTEXT;
}

function isWizardContext(
  value: string | null | undefined
): value is WizardContext {
  return WIZARD_CONTEXTS.some(context => context === value);
}

/** The modes offered wherever the wizard mode can be switched. */
export function useWizardContextOptions(): Array<{
  value: WizardContext;
  label: string;
}> {
  const { t } = useTranslate(["QuoteEdit", "Decisions"]);

  return [
    { value: "item", label: t("Item", { ns: "QuoteEdit" }) },
    {
      value: "onQuoteCreate",
      label: t("Quote create wizard", { ns: "Decisions" }),
    },
    {
      value: "onQuoteRefinement",
      label: t("Quote refinement wizard", { ns: "Decisions" }),
    },
    {
      value: "onQuoteRefinement2",
      label: t("Quote refinement wizard2", { ns: "Decisions" }),
    },
    {
      value: "onQuoteRefinement3",
      label: t("Quote refinement wizard3", { ns: "Decisions" }),
    },
  ];
}

export function useWizardContext(document: {
  viewerPermissions: PermissionName[];
}) {
  const { t } = useTranslate(["QuoteEdit"]);

  const [storedContext, setStoredContext] =
    useLocalStorageAsState<WizardContext>(
      "quote-wizard-active-context",
      DEFAULT_WIZARD_CONTEXT
    );

  const [wizardModeParam, setWizardModeParam] = useStateWithUrlParam<
    string | null
  >(WIZARD_MODE_URL_PARAM, null);

  const activeContext = resolveWizardContext(wizardModeParam, storedContext);

  const setActiveContext = React.useCallback(
    (context: WizardContext) => {
      setStoredContext(context);
      setWizardModeParam(context);
    },
    [setStoredContext, setWizardModeParam]
  );

  const wizardVisibilityOptions = useWizardContextOptions();

  const selectContextButton = (
    <RestrictedByDocumentPermissionWithDebug
      permission="quote_decisions_decide"
      document={document}
    >
      <ButtonSelect
        label={t("Wizard", { ns: "QuoteEdit" })}
        options={wizardVisibilityOptions}
        value={activeContext}
        searchLabel=""
        noOptionsLabel=""
        onChange={value => {
          if (value) {
            setActiveContext(value);
          }
        }}
        showCount={false}
      />
    </RestrictedByDocumentPermissionWithDebug>
  );

  return { selectContextButton, activeContext, setActiveContext };
}

/** The document a wizard-question marker set is read for. */
export type WizardQuestionsDoc =
  | { type: "quote"; quoteId: string }
  | { type: "requirement"; requirementId: string };

export function useTreeItemIdsWithWizardQuestions({
  projectId,
  doc,
  wizardContext,
}: {
  projectId: string;
  doc: WizardQuestionsDoc;
  wizardContext: WizardContext;
}) {
  const strategy = wizardContextStrategies[wizardContext];

  const quote = useQuoteWizardQuestionsQuery({
    variables: {
      projectId,
      quoteId: doc.type === "quote" ? doc.quoteId : "",
      strategy,
    },
    skip: doc.type !== "quote",
  });

  const requirement = useRequirementWizardQuestionsQuery({
    variables: {
      projectId,
      requirementId: doc.type === "requirement" ? doc.requirementId : "",
      strategy,
    },
    skip: doc.type !== "requirement",
  });

  const result =
    doc.type === "quote"
      ? quote.data?.quoteWizardQuestions
      : requirement.data?.requirementWizardQuestions;

  const itemIdsWithQuestion = React.useMemo(() => {
    if (
      result?.__typename !== "QuoteWizardQuestionsResult" &&
      result?.__typename !== "RequirementWizardQuestionsResult"
    ) {
      return [];
    }

    return result.questions.map(q => q.itemId);
  }, [result]);

  const hasItemAnyQuestions = React.useCallback(
    (itemId: string) => itemIdsWithQuestion.includes(itemId),
    [itemIdsWithQuestion]
  );

  const refetch = doc.type === "quote" ? quote.refetch : requirement.refetch;

  const memoizedRefetch = React.useCallback(async () => {
    await refetch();
  }, [refetch]);

  return [hasItemAnyQuestions, memoizedRefetch] as const;
}

export const wizardContextStrategies: Record<
  WizardContext,
  WizardQuestionStrategy
> = {
  item: "ITEM_EACH",
  onQuoteCreate: "ON_QUOTE_CREATE",
  onQuoteRefinement: "ON_QUOTE_REFINEMENT",
  onQuoteRefinement2: "ON_QUOTE_REFINEMENT_2",
  onQuoteRefinement3: "ON_QUOTE_REFINEMENT_3",
};
